import {Injectable} from '@angular/core';
import {SeAnalyticsService} from 'se-analytics';
import {SeAnalyticsEvent} from 'se-analytics-js';
import {PageAttributes} from '../models/page-attributes';
import {getAnalyticsCoreScriptSrc, isAnalyticsDebugEnabled} from '../observability/analytics.config';

const analyticsCoreScriptSrc = getAnalyticsCoreScriptSrc();
const scriptStatusDataKey = 'seProductManagementAnalyticsStatus';

type AnalyticsPrimitive = string | number | boolean | null | undefined;

export const AnalyticsEvents = {
  ActivateBundle: 'activate_bundle',
  ActivateProduct: 'activate_product',
  AddBundle: 'add_bundle',
  AddBundleMedia: 'add_bundle_media',
  AddBundleProduct: 'add_bundle_product',
  AddProduct: 'add_product',
  AddProductMedia: 'add_product_media',
  AddProductVariant: 'add_product_variant',
  CancelAddBundle: 'cancel_add_bundle',
  CancelAddProduct: 'cancel_add_product',
  CreateProductBundleClicked: 'create_product_bundle_clicked',
  DeactivateBundle: 'deactivate_bundle',
  DeactivateProduct: 'deactivate_product',
  RemoveBundleProduct: 'remove_bundle_product',
} as const;

/** Custom event payload values accepted by {@link AnalyticsService.trackAction}. */
export type AnalyticsEventProperties = Record<string, AnalyticsPrimitive>;

/** Load state of the lazy-loaded se-analytics-core script. */
export type AnalyticsCoreScriptStatus = 'loaded' | 'loading' | 'failed' | 'missing';

/** Runtime state of the global `window.seAnalytics` host object. */
export type AnalyticsHostStatus = 'running' | 'queued' | 'unavailable';

/** Snapshot of analytics integration state exposed for local debugging. */
export interface AnalyticsDebugStatus {
  hostStatus: AnalyticsHostStatus;
  coreScriptStatus: AnalyticsCoreScriptStatus;
  coreScriptSrc: string;
  lastEventAction?: string;
  lastEventQueued: boolean;
}

/** Minimal shape of the initialized `window.seAnalytics` object. */
interface SeAnalyticsQueue {
  push(event: SeAnalyticsEvent): unknown;
}

type AnalyticsGlobal = typeof globalThis & {
  seAnalytics?: SeAnalyticsQueue | SeAnalyticsEvent[];
  seProductManagementAnalyticsStatus?: () => AnalyticsDebugStatus;
};

/**
 * Wraps the se-analytics package for Product Management UI event tracking.
 * Pushes `seEvent` payloads via the package `push` API and exposes debug helpers
 * when analytics debug mode is enabled.
 */
@Injectable({providedIn: 'root'})
export class AnalyticsService {
  private lastEventStatus: Pick<AnalyticsDebugStatus, 'lastEventAction' | 'lastEventQueued'> = {
    lastEventQueued: false,
  };
  private packageProcessQueue: SeAnalyticsService['processQueue'];

  constructor(private seAnalytics: SeAnalyticsService) {
    this.packageProcessQueue = this.seAnalytics.processQueue;
    this.installCoreScriptStatusListeners();
    this.installStatusCheck();
    this.logDebug('initialized');
  }

  /**
   * Track a custom analytics action.
   * Events are sent through se-analytics `push` as `{ type: 'seEvent', data }`.
   * Failures are swallowed so tracking never interrupts the user flow.
   *
   * @param action - Action name recorded in event data (for example, `add_bundle`).
   * @param properties - Additional event properties merged into the payload.
   */
  trackAction(action: string, properties: AnalyticsEventProperties = {}): void {
    // eslint-disable-next-line no-useless-assignment
    let eventQueued = false;
    const event = {
      type: 'seEvent' as const,
      data: {...properties, action},
    };

    try {
      this.seAnalytics.push(event);
      eventQueued = true;
    } catch {
      eventQueued = false;
    }

    this.lastEventStatus = {
      lastEventAction: action,
      lastEventQueued: eventQueued,
    };
    this.logDebug(`tracked ${action}`, {event, queued: eventQueued});
  }

  /**
   * Track a page view through se-analytics `push` as `{ type: 'pageView', data }`.
   * Failures are swallowed so tracking never interrupts the user flow.
   */
  trackPageView(attributes: PageAttributes): void {
    // eslint-disable-next-line no-useless-assignment
    let eventQueued = false;
    const event = {
      type: 'pageView' as const,
      data: attributes,
    };

    try {
      this.seAnalytics.push(event);
      eventQueued = true;
    } catch {
      eventQueued = false;
    }

    this.lastEventStatus = {
      lastEventAction: attributes.title ?? attributes.page ?? 'pageView',
      lastEventQueued: eventQueued,
    };
    this.logDebug('tracked pageView', {event, queued: eventQueued});
  }

  /**
   * Reports whether `window.seAnalytics` is initialized, still a raw queue, or absent.
   */
  getHostStatus(): AnalyticsHostStatus {
    const seAnalytics = this.analyticsGlobal.seAnalytics;
    if (Array.isArray(seAnalytics)) {
      return 'queued';
    }

    if (seAnalytics && typeof (seAnalytics as SeAnalyticsQueue).push === 'function') {
      return 'running';
    }

    return 'unavailable';
  }

  /**
   * Reports whether the se-analytics-core script is present and has finished loading.
   */
  getCoreScriptStatus(): AnalyticsCoreScriptStatus {
    const script = this.analyticsCoreScript;
    if (!script) {
      return 'missing';
    }

    const scriptStatus = script.dataset[scriptStatusDataKey];
    if (scriptStatus === 'loaded' || this.hasLoadedCore()) {
      return 'loaded';
    }

    if (scriptStatus === 'failed') {
      return 'failed';
    }

    return 'loading';
  }

  /**
   * Returns the current analytics integration snapshot for console or browser debugging.
   */
  getDebugStatus(): AnalyticsDebugStatus {
    return {
      hostStatus: this.getHostStatus(),
      coreScriptStatus: this.getCoreScriptStatus(),
      coreScriptSrc: analyticsCoreScriptSrc,
      ...this.lastEventStatus,
    };
  }

  /** Typed view of browser globals used by analytics debugging. */
  private get analyticsGlobal(): AnalyticsGlobal {
    return globalThis as AnalyticsGlobal;
  }

  /** Locates the injected se-analytics-core script tag, if present. */
  private get analyticsCoreScript(): HTMLScriptElement | null {
    if (typeof document === 'undefined') {
      return null;
    }

    return (
      Array.from(document.scripts).find(script => {
        return script.src === analyticsCoreScriptSrc || script.src.includes('/se-analytics-core/main.js');
      }) ?? null
    );
  }

  /** Tracks se-analytics-core script load and error events on its dataset. */
  private installCoreScriptStatusListeners(): void {
    const script = this.analyticsCoreScript;
    if (!script || script.dataset[scriptStatusDataKey]) {
      return;
    }

    script.dataset[scriptStatusDataKey] = this.hasLoadedCore() ? 'loaded' : 'loading';
    script.addEventListener(
      'load',
      () => {
        script.dataset[scriptStatusDataKey] = 'loaded';
        this.logDebug('core script loaded');
      },
      {once: true}
    );
    script.addEventListener(
      'error',
      () => {
        script.dataset[scriptStatusDataKey] = 'failed';
        this.logDebug('core script failed');
      },
      {once: true}
    );
  }

  /** True once se-analytics-core replaces the package noop `processQueue` implementation. */
  private hasLoadedCore(): boolean {
    return this.seAnalytics.processQueue !== this.packageProcessQueue;
  }

  /** Exposes `window.seProductManagementAnalyticsStatus` when analytics debug is enabled. */
  private installStatusCheck(): void {
    if (!isAnalyticsDebugEnabled()) {
      return;
    }

    this.analyticsGlobal.seProductManagementAnalyticsStatus = () => this.getDebugStatus();
  }

  /** Writes structured analytics debug output to the browser console. */
  private logDebug(message: string, details: Record<string, unknown> = {}): void {
    if (!isAnalyticsDebugEnabled()) {
      return;
    }

    // eslint-disable-next-line no-console
    console.info(`[PMUI analytics] ${message}`, {
      ...details,
      status: this.getDebugStatus(),
    });
  }
}
